import { createElement } from "react";
import type { ShellNavItem } from "@void-orbit/ui";
import { Icon, Laptop, AddressBookTabs, Chats, PresentationChart, CalendarPlus } from "@phosphor-icons/react";
import { UserRole } from "../../../models/User";
import { hasRole, roleGroups } from "../../../services/accessControl";

export interface MenuItemConfig {
  Icon: Icon;
  label: string;
  roles: ReadonlyArray<UserRole>;
  path?: string;
  nestedPaths?: Array<string>;
  subItems?: Array<MenuItemConfig>;
};

export const menuItems: Array<MenuItemConfig> = [
  {
    Icon: PresentationChart,
    label: "Dashboard",
    path: "/",
    roles: roleGroups.dashboard,
  },
  {
    Icon: Chats,
    label: "Conversas",
    path: "/chats",
    nestedPaths: [ "/chat" ],
    roles: roleGroups.chat,
  },
  {
    Icon: CalendarPlus,
    label: "Envios Agendados",
    path: "/shipments",
    roles: roleGroups.shipments,
  },
  {
    Icon: AddressBookTabs,
    label: "Contatos",
    path: "/contacts",
    roles: roleGroups.contacts,
  },
  {
    Icon: Laptop,
    label: "Registro de Logs",
    path: "/logs",
    roles: roleGroups.logs,
  },
];

const routeMatches = ( pathname: string, route: string ) => (
  route === "/" ? pathname === route : pathname === route || pathname.startsWith(`${ route }/`)
);

export const isMenuItemSelected = ({ path = "", nestedPaths = [] }: MenuItemConfig, pathname: string ) => {
  return Boolean( path && routeMatches( pathname, path ))
    || nestedPaths.some(( nestedPath ) => routeMatches( pathname, nestedPath ));
};

export const getPageTitle = ( pathname: string, role?: UserRole ) => {
  if( pathname === "/" ) return role === UserRole.ATTENDANT ? "Conversas" : "Dashboard";
  if( routeMatches( pathname, "/chats" ) || routeMatches( pathname, "/chat" )) return "Conversas";
  if( routeMatches( pathname, "/shipments" )) return "Envios Agendados";
  if( routeMatches( pathname, "/contacts" )) return "Contatos";
  if( routeMatches( pathname, "/logs" )) return "Registro de Logs";

  return "Workspace";
};

// The Orbit owns channel administration since front D.
export const orbitChannelsURL = ( organizationId?: string ): string | null => {
  const base = ( process.env.REACT_APP_VOID_ORBIT_URL ?? "" ).trim().replace( /\/+$/, "" );
  if( !base || !organizationId ) return null;
  return `${ base }/organizations/${ encodeURIComponent( organizationId ) }/products/void-chat/channels`;
};

export const manageChannelsURL = ( role: UserRole | undefined, organizationId?: string ): string | null => (
  role === UserRole.ADMIN ? orbitChannelsURL( organizationId ) : null
);

export const buildShellNav = (
  items: Array<MenuItemConfig>,
  role: UserRole | undefined,
  pathname: string,
  navigate: ( path: string ) => void,
): Array<ShellNavItem> => items
  .filter(( item ) => hasRole( role, item.roles ))
  .map(( item ) => ({
    id: item.label,
    label: item.label,
    icon: createElement( item.Icon, { size: 24, weight: "duotone" }),
    selected: isMenuItemSelected( item, pathname ) || ( role === UserRole.ATTENDANT && pathname === "/" && item.path === "/chats" ),
    onSelect: item.subItems ? undefined : () => navigate( item.path ?? "" ),
    children: item.subItems ? buildShellNav( item.subItems, role, pathname, navigate ) : undefined,
  }));

export const shouldLoadMoreNotifications = ( loading: boolean, loaded: number, total: number ) => !loading && loaded < total;
